import { Link, useParams } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, Clock } from "lucide-react";
import ARTICLES from "../guides/articles.json";
import SEO_ROUTES from "../seo-routes.json";
import { Seo, SITE_URL, breadcrumbSchema } from "../components/site/Seo";
import { ContactActions } from "../components/site/ContactActions";
import NotFound from "./NotFound";

const fmtDate = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

function Block({ b, i }) {
  if (b.type === "h2") return <h2 className="mt-10 font-[Poppins] font-bold text-[22px] md:text-[26px] text-[var(--pg-navy)] leading-tight">{b.text}</h2>;
  if (b.type === "p") return <p className="mt-4 text-[16.5px] text-[var(--pg-ink-soft)] leading-[1.75]">{b.text}</p>;
  if (b.type === "checklist") {
    return (
      <ul className="mt-5 bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6 space-y-3" data-testid={`guide-checklist-${i}`}>
        {b.items.map((it) => <li key={it} className="flex items-start gap-3 text-[15.5px] text-[var(--pg-navy)] font-medium"><CheckCircle2 size={19} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{it}</li>)}
      </ul>
    );
  }
  return (
    <ul className="mt-4 space-y-2.5 pl-1">
      {b.items.map((it) => <li key={it} className="flex items-start gap-3 text-[16px] text-[var(--pg-ink-soft)] leading-relaxed"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[var(--pg-accent)] flex-shrink-0" />{it}</li>)}
    </ul>
  );
}

export function GuidesIndex() {
  return (
    <div data-testid="guides-page">
      <Seo route="/guides" jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }])} />
      <section className="pt-[130px] pb-14 hero-bg">
        <div className="pg-container max-w-4xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>Buyer Guides</span>
          <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">{SEO_ROUTES["/guides"].h1}</h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-2xl leading-relaxed">
            Short, practical answers to the questions purchasing officers and buyers ask before sending a requirement — written by our procurement team, no fluff.
          </p>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-[var(--pg-bg)]">
        <div className="pg-container max-w-4xl grid gap-5">
          {ARTICLES.map((a) => (
            <Link key={a.slug} to={`/guides/${a.slug}`} data-testid={`guide-card-${a.slug}`} className="lift-card bg-white border border-slate-200 rounded-2xl p-7 block">
              <div className="flex items-center gap-2 text-[12px] font-[Poppins] font-semibold uppercase tracking-wider text-[var(--pg-accent)]"><BookOpen size={14} /> Guide · <Clock size={13} /> {a.readingMinutes} min read</div>
              <h2 className="mt-3 font-[Poppins] font-bold text-[22px] md:text-[26px] text-[var(--pg-navy)] leading-tight">{a.title}</h2>
              <p className="mt-3 text-[15.5px] text-[var(--pg-ink-soft)] leading-relaxed">{a.summary}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-[Poppins] font-semibold text-[14px] text-[var(--pg-navy)]">Read the guide <ArrowRight size={15} /></span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function GuideArticle() {
  const { slug } = useParams();
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) return <NotFound />;
  const route = `/guides/${a.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.summary,
      datePublished: a.published, dateModified: a.updated, url: `${SITE_URL}${route}`,
      author: { "@type": "Organization", name: "Patrick Global Procurement Services", url: SITE_URL },
      publisher: { "@id": `${SITE_URL}/#organization` }, mainEntityOfPage: `${SITE_URL}${route}`,
      image: `${SITE_URL}/assets/pgps-logo.jpg`,
    },
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }, { name: a.title, path: route }]),
  ];
  return (
    <div data-testid={`guide-article-${a.slug}`}>
      <Seo route={route} jsonLd={jsonLd} />
      <section className="pt-[130px] pb-14 hero-bg">
        <div className="pg-container max-w-4xl">
          <nav aria-label="Breadcrumb" className="text-[13px] text-white/70 flex items-center gap-2" data-testid="guide-breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link> <span>/</span> <Link to="/guides" className="hover:text-white">Guides</Link>
          </nav>
          <h1 className="mt-5 text-[34px] md:text-[48px] font-extrabold leading-[1.08] text-white">{a.title}</h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-3xl leading-relaxed">{a.summary}</p>
          <p className="mt-5 text-[13px] text-white/60" data-testid="guide-meta">For: {a.audience} · {a.readingMinutes} min read · Updated {fmtDate(a.updated)}</p>
        </div>
      </section>
      <article className="py-14 md:py-20 bg-white">
        <div className="pg-container max-w-3xl" data-testid="guide-body">
          {a.blocks.map((b, i) => <Block key={i} b={b} i={i} />)}
          <div className="mt-12 rounded-2xl bg-gradient-to-r from-[var(--pg-navy)] to-[var(--pg-navy-2)] text-white p-8">
            <h2 className="font-[Poppins] font-extrabold text-[24px] leading-tight">Ready to send a requirement?</h2>
            <p className="mt-2 text-white/85 text-[15.5px]">Use the RFQ form to attach your list or solicitation — a procurement specialist responds within one business day.</p>
            <ContactActions source="guide_cta" className="mt-6" />
          </div>
          <p className="mt-8 text-[13.5px] text-slate-500">Related: <Link to="/government" className="text-[var(--pg-navy)] font-semibold hover:text-[var(--pg-accent)]">Government Contracting &amp; Capabilities</Link> · <Link to="/procurement" className="text-[var(--pg-navy)] font-semibold hover:text-[var(--pg-accent)]">Procurement &amp; Sourcing</Link> · <Link to="/guides" className="text-[var(--pg-navy)] font-semibold hover:text-[var(--pg-accent)]">All guides</Link></p>
        </div>
      </article>
    </div>
  );
}
